import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useTranslation } from 'react-i18next';
import NoteDetail from './NoteDetail';
import { trackNoteDeleted } from '../analytics';

/**
 * Standalone note detail view for use in a popup window (e.g. opened by double-click in Notes list).
 * Fetches the note by ID, verifies ownership, and renders NoteDetail. onClose closes the window.
 */
function NoteDetailWindow() {
  const { noteId } = useParams();
  const { t } = useTranslation();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!noteId || !auth.currentUser) {
      setLoading(false);
      if (!auth.currentUser) setError('auth');
      else setError('notFound');
      return;
    }

    const fetchNote = async () => {
      try {
        const noteRef = doc(db, 'notes', noteId);
        const noteSnap = await getDoc(noteRef);
        if (!noteSnap.exists()) {
          setError('notFound');
          setLoading(false);
          return;
        }
        const data = noteSnap.data();
        if (data.uid !== auth.currentUser.uid) {
          setError('forbidden');
          setLoading(false);
          return;
        }
        setNote({ id: noteSnap.id, ...data });
      } catch (err) {
        console.error('Error fetching note for window:', err);
        setError('error');
      } finally {
        setLoading(false);
      }
    };

    fetchNote();
  }, [noteId]);

  const handleClose = () => {
    window.close();
  };

  const handleDelete = (deletedNoteId) => {
    trackNoteDeleted(deletedNoteId);
    window.close();
  };

  if (!auth.currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  if (error === 'notFound' || error === 'forbidden') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <p className="text-gray-700 mb-4">
          {t('notes.detail.noNoteAvailable')}
        </p>
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 bg-[#F07807] hover:bg-[#d66a05] text-white rounded-md"
        >
          {t('common.close')}
        </button>
      </div>
    );
  }

  if (error === 'error' || !note) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <p className="text-gray-700 mb-4">{t('notes.detail.unknownError')}</p>
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 bg-[#F07807] hover:bg-[#d66a05] text-white rounded-md"
        >
          {t('common.close')}
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      <NoteDetail
        note={note}
        onClose={handleClose}
        onDelete={handleDelete}
        variant="sidebar"
      />
    </div>
  );
}

export default NoteDetailWindow;
